import { useQuery } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type {
  AgentPerformanceRaw,
  BehavioralContestationRaw,
  BehavioralPatternRaw,
  OperatorQualityRaw,
  PlaybookDriftRaw,
  PlaybookQualityRaw,
  QualityGlobalRaw,
  ResolutionBySwitchBucketRaw,
  SequenceFlagsRaw,
  TopAttendantAbandonedRaw,
  TopBondCriticalRaw,
} from '../types';

export const QUALITY_KEY = ['quality'] as const;

/** Janela temporal derivada do chip de período (D5). */
export interface QualityWindow {
  periodFrom: string;
  periodTo: string;
}

// Por ora sem paginação infinita — limit alto (honesto se truncar; T6-3 valida volume).
const OPERATOR_LIMIT = 200;

export function useQualityGlobal(periodType: 'daily' | 'weekly' | 'monthly'): {
  data: QualityGlobalRaw[] | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'global', periodType],
    queryFn: () => api.getQualityGlobal({ periodType }),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

// Limite alto p/ padrões/contestações (sem paginação infinita por ora; T6-3 valida volume).
const BEHAVIORAL_LIMIT = 200;

export function useQualitySequences(w: QualityWindow): {
  data: SequenceFlagsRaw | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'sequences', w.periodFrom, w.periodTo],
    queryFn: () => api.getQualitySequences({ periodFrom: w.periodFrom, periodTo: w.periodTo }),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

export function useQualityPlaybookDrift(w: QualityWindow): {
  data: PlaybookDriftRaw[] | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'playbook-drift', w.periodFrom, w.periodTo],
    queryFn: () => api.getQualityPlaybookDrift({ periodFrom: w.periodFrom, periodTo: w.periodTo }),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

export function useQualityTopAttendants(w: QualityWindow): {
  data: TopAttendantAbandonedRaw[] | undefined;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'top-attendants', w.periodFrom, w.periodTo],
    queryFn: () => api.getQualityTopAttendants({ periodFrom: w.periodFrom, periodTo: w.periodTo }),
    staleTime: 60 * 1000,
  });
  return { data: q.data };
}

export function useQualityTopBonds(w: QualityWindow): {
  data: TopBondCriticalRaw[] | undefined;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'top-bonds', w.periodFrom, w.periodTo],
    queryFn: () => api.getQualityTopBonds({ periodFrom: w.periodFrom, periodTo: w.periodTo }),
    staleTime: 60 * 1000,
  });
  return { data: q.data };
}

export function useQualityResolutionBySwitches(w: QualityWindow): {
  data: ResolutionBySwitchBucketRaw[] | undefined;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'resolution-by-switches', w.periodFrom, w.periodTo],
    queryFn: () =>
      api.getQualityResolutionBySwitches({ periodFrom: w.periodFrom, periodTo: w.periodTo }),
    staleTime: 60 * 1000,
  });
  return { data: q.data };
}

/** Só padrões ativos (D — Seção 6 filtra `status==='active'`). */
export function useBehavioralPatterns(): {
  data: { items: BehavioralPatternRaw[]; nextCursor: string | null } | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'behavioral-patterns', 'active'],
    queryFn: () => api.getBehavioralPatterns({ status: 'active', limit: BEHAVIORAL_LIMIT }),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

/** Contestações pendentes (contagem + nomes na Seção 6). */
export function useBehavioralContestations(): {
  data: { items: BehavioralContestationRaw[]; nextCursor: string | null } | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'behavioral-contestations', 'pending'],
    queryFn: () => api.getBehavioralContestations({ status: 'pending', limit: BEHAVIORAL_LIMIT }),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

export function useQualityByOperator(w: QualityWindow): {
  data: { items: OperatorQualityRaw[]; nextCursor: string | null } | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'by-operator', w.periodFrom, w.periodTo],
    queryFn: () =>
      api.getQualityByOperator({
        periodFrom: w.periodFrom,
        periodTo: w.periodTo,
        limit: OPERATOR_LIMIT,
      }),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

export function useQualityByPlaybook(w: QualityWindow): {
  data: PlaybookQualityRaw[] | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'by-playbook', w.periodFrom, w.periodTo],
    queryFn: () => api.getQualityByPlaybook({ periodFrom: w.periodFrom, periodTo: w.periodTo }),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

export function useQualityAgents(w: QualityWindow): {
  data: AgentPerformanceRaw[] | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...QUALITY_KEY, 'agents', w.periodFrom, w.periodTo],
    queryFn: () => api.getQualityAgents({ periodFrom: w.periodFrom, periodTo: w.periodTo }),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}
